/* eslint-disable react/display-name */
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Flex,
  Skeleton,
  useDisclosure,
  Portal,
  useToast,
} from '@chakra-ui/react';
import {
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
} from '@tanstack/react-table';
import React, { useMemo, useCallback } from 'react';

import {
  utils as commonUtils,
  date as commonDate,
} from '@finley-technologies/common';

import {
  LimitedPartnerCommitmentDetailGQLType,
  GetCommitmentsForLimitedPartnerQueryGQLType,
  useUpdateLimitedPartnerMappingMutation,
  GetCommitmentsForLimitedPartnerDocumentGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { Typography } from 'packages/common-frontend/components/Typography';
import { TableBETA } from 'packages/common-frontend/tables/TableBETA';
import { colors } from '~/packages/common-frontend/colors';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Timestamp } from '~/packages/common-frontend/components/Timestamp';
import {
  successToastConfig,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import { remToPx } from '~/packages/common-frontend/theme/utils';

export const LPCommitmentMenu = ({
  unmatchCommitment,
}: {
  unmatchCommitment: () => void;
}) => {
  const menuControls = useDisclosure();

  return (
    <>
      <Menu {...menuControls} computePositionOnMount strategy="fixed">
        <MenuButton
          as={IconButtonV2}
          variant="secondary-muted"
          colorScheme="gray"
          isActive={menuControls.isOpen}
          aria-label="LP Commitment options"
          icon={<Icon icon="ellipsis-vertical" />}
          tooltipEnabled={false}
        />
        <Portal>
          <MenuList zIndex="dropdown">
            <MenuItem onClick={unmatchCommitment} color={colors.gray[800]}>
              Unmatch LP Alias
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>
    </>
  );
};

export const LimitedPartnerDetailCommitmentsTable = ({
  commitmentData,
  handleUnmatch,
}: {
  commitmentData: LimitedPartnerCommitmentDetailGQLType[];
  handleUnmatch: ({ mappingId }: { mappingId: string }) => void;
}) => {
  const columnHelper = createColumnHelper<LimitedPartnerCommitmentDetailGQLType>();
  const columns = useMemo(() => {
    const columnDefs = [
      columnHelper.accessor('creditFacilityName', {
        header: 'Facility',
      }),
      columnHelper.accessor('borrowerName', {
        header: 'Borrower',
      }),
      columnHelper.accessor('limitedPartnerAlias', {
        header: 'LP Alias',
      }),
      columnHelper.accessor('committedCapital', {
        header: 'Committed Capital',
        minSize: remToPx(10),
        size: remToPx(10),
        cell: ({ getValue }) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: getValue(),
          }),
      }),
      columnHelper.accessor('recallableCapital', {
        header: 'Recallable Capital',
        minSize: remToPx(10),
        size: remToPx(10),
        cell: ({ getValue }) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: getValue(),
          }),
      }),
      columnHelper.accessor('uncalledCapital', {
        header: 'Uncalled Capital',
        minSize: remToPx(10),
        size: remToPx(10),
        cell: ({ getValue }) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: getValue(),
          }),
      }),
      columnHelper.accessor('eligibleUncalledCapitalLessKickouts', {
        header: 'Eligible Uncalled Capital Less Kickouts',
        minSize: remToPx(13),
        size: remToPx(13),
        cell: ({ getValue }) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: getValue(),
          }),
      }),
      columnHelper.accessor('netUncalledCapital', {
        header: 'Net Uncalled Capital',
        minSize: remToPx(10),
        size: remToPx(10),
        cell: ({ getValue }) =>
          commonUtils.numberToCurrencyWithSymbol({
            number: getValue(),
          }),
      }),
      columnHelper.accessor('percentageOfNetUncalledCapital', {
        header: '% of Net Uncalled Capital',
        minSize: remToPx(10),
        size: remToPx(10),
        cell: ({ getValue }) =>
          commonUtils.numberToFormattedString(getValue(), 'percentage', false),
      }),
      columnHelper.accessor('reportingDate', {
        header: 'Reporting Date',
        cell: ({ getValue }) =>
          commonDate.fullDateStrToHumanDateString(getValue()),
      }),
      columnHelper.accessor(
        (row) => row.matchedByUserName || row.matchedByUserEmail,
        {
          header: 'Matched By',
          minSize: remToPx(10),
          size: remToPx(10),
          cell: ({ getValue, row }) => {
            return (
              <Flex direction="column">
                <Typography variant="base-muted">{getValue()}</Typography>
                <Timestamp
                  timestamp={row.original.matchedAt}
                  variant="datetime"
                />
              </Flex>
            );
          },
        },
      ),
      columnHelper.display({
        id: 'menu',
        minSize: remToPx(4),
        size: remToPx(4),
        cell: ({ row }) => {
          return (
            <LPCommitmentMenu
              unmatchCommitment={() => {
                handleUnmatch({
                  mappingId: row.original.mappingId,
                });
              }}
            />
          );
        },
      }),
    ];

    return columnDefs;
  }, [columnHelper, handleUnmatch]);

  const table = useReactTable({
    data: commitmentData,
    columns,
    initialState: {
      columnPinning: { right: ['menu'] },
    },
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    getRowId: (row) => row.finleyReceivableId,
  });

  return <TableBETA table={table} FlexContainerProps={{ maxHeight: '70vh' }} />;
};

export const LimitedPartnerDetailsCommitmentsTableContainer = ({
  commitmentData,
  refetchingCommitments,
  setRefetchingCommitments,
  refetchLimitedPartnerCommitments,
}: {
  commitmentData: GetCommitmentsForLimitedPartnerQueryGQLType | undefined;
  refetchingCommitments: boolean;
  setRefetchingCommitments: React.Dispatch<React.SetStateAction<boolean>>;
  refetchLimitedPartnerCommitments: () => Promise<void>;
}) => {
  const toast = useToast();

  const [updateLimitedPartnerMapping] = useUpdateLimitedPartnerMappingMutation({
    onCompleted: (data) => {
      if (data && data.updateLimitedPartnerMapping) {
        const {
          limitedPartnerAlias,
          limitedPartnerName,
          limitedPartnerMappingId,
        } = data.updateLimitedPartnerMapping;
        successToastConfig.id = `unmatch-limited-partner-toast-${limitedPartnerMappingId}`;
        successToastConfig.title = `${limitedPartnerAlias} was unmatched from ${limitedPartnerName}.`;
        toast(successToastConfig);
      }
    },
    onError: (err) => {
      errorToastConfig.title = err.message;
      toast(errorToastConfig);
    },
    refetchQueries: [GetCommitmentsForLimitedPartnerDocumentGQLType],
  });

  const handleUnmatch = useCallback(
    async ({ mappingId }: { mappingId: string }) => {
      setRefetchingCommitments(true);
      await updateLimitedPartnerMapping({
        variables: {
          limitedPartnerMappingId: mappingId,
          limitedPartnerId: null,
        },
      });
      await refetchLimitedPartnerCommitments();
      setRefetchingCommitments(false);
    },
    [
      refetchLimitedPartnerCommitments,
      setRefetchingCommitments,
      updateLimitedPartnerMapping,
    ],
  );

  return (
    <Flex direction="column" gap="1rem" mt="2.5rem">
      <Typography variant="section-title" trim>
        Limited Partner Commitments
      </Typography>
      {commitmentData && !refetchingCommitments ? (
        <LimitedPartnerDetailCommitmentsTable
          commitmentData={commitmentData.limitedPartnerCommitments}
          handleUnmatch={handleUnmatch}
        />
      ) : (
        <Skeleton height="60vh" />
      )}
    </Flex>
  );
};
